<script setup lang="ts">
import { inject } from "vue";
import { backgroundColorInjectKey } from "../helpers/injectKeys";
import type { BlokDateEntry } from "@/.storyblok/types/120421/storyblok-components";

const props = defineProps<{
  blok: BlokDateEntry;
}>();

const injectedBackgroundColor = inject(
  backgroundColorInjectKey
) as BackgroundColorKey;
</script>

<template>
  <div
    v-editable="blok"
    class="timeline-entry"
    :class="{
      'timeline-entry--dark': injectedBackgroundColor === 'light',
      'timeline-entry--light':
        injectedBackgroundColor === 'dark' ||
        injectedBackgroundColor === 'medium',
    }"
  >
    <div
      class="timeline-entry__box"
      :class="{
        'timeline-entry__box--dark': injectedBackgroundColor === 'light',
        'timeline-entry__box--light':
          injectedBackgroundColor === 'dark' ||
          injectedBackgroundColor === 'medium',
      }"
    >
      <div class="timeline-entry__box-inner">
        <div class="font-optimus font-normal text-xl">
          {{ $d(new Date(blok.dateStart), "short") }} -
          {{ $d(new Date(blok.dateEnd), "long") }}
        </div>
        <div v-if="blok.note" class="text-sm mt-2 text-darkgrey">
          {{ blok.note }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.timeline-entry {
  display: flex;
  align-items: center;

  &::before {
    @apply bg-brandLight;
    content: "";
    width: 16px;
    height: 16px;
    border-radius: 100%;
    flex-shrink: 0;
    transform: translateX(-50%);
  }

  &--light {
    &::before {
      @apply bg-brandLight;
    }
  }
  &--dark {
    &::before {
      @apply bg-brandDark;
    }
  }

  @screen sm {
    &:nth-child(odd) {
      transform: translate(50%);
    }

    &:nth-child(even) {
      transform: translateX(-50%) scaleX(-1);
    }
    &:nth-child(even) &__box-inner {
      transform: scaleX(-1);
    }
  }

  &__box {
    @apply bg-brandLight;
    margin-left: 20px;
    position: relative;
    width: 100%;
    padding: 12px;
    border-radius: 8px;

    &:before {
      @apply border-r-brandLight;
      content: "";
      width: 0;
      height: 0;
      border-top: 10px solid transparent;
      border-bottom: 10px solid transparent;
      border-right-width: 10px;
      border-right-style: solid;
      position: absolute;
      top: 50%;
      left: 0;
      transform: translate(-100%, -50%);
    }

    &--light {
      @apply bg-brandLight;

      &::before {
        @apply border-r-brandLight;
      }
    }
    &--dark {
      @apply bg-brandDark;

      &::before {
        @apply border-r-brandDark;
      }
    }
  }
}
</style>
